import { inputRules, wrappingInputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import { Fragment, Slice } from 'prosemirror-model';
import { liftListItem, sinkListItem, splitListItem, wrapInList } from 'prosemirror-schema-list';
import { Plugin } from 'prosemirror-state';
import { Transform } from 'prosemirror-transform';
import { EditorView } from 'prosemirror-view';

import { isMacOS } from '@recruitee/user-interface';

import { EditorButton, RtPMPlugin } from '../../editor/types';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY } from '../../helpers/utils';
import { ParagraphNode } from '../basic/nodes';
import { ListService } from './list-service-public';
import { BulletListNode, ListSchema, OrderedListNode } from './nodes';

export class ListPlugin implements RtPMPlugin {
  public api: {
    orderedListService: ListService;
    bulletListService: ListService;
  };

  public buttons = {
    toggleBulletList: <EditorButton>{
      icon: 'format-list-bulleted',
      isActive: (...args) => this.api.bulletListService.isActive(...args),
      run: (state, dispatch) => {
        this.api.bulletListService.toggle(state, dispatch);
      },
    },
    toggleOrderedList: <EditorButton>{
      icon: 'format-list-numbered',
      isActive: (...args) => this.api.orderedListService.isActive(...args),
      run: (state, dispatch) => {
        this.api.orderedListService.toggle(state, dispatch);
      },
    },
  };

  private editorView: EditorView<ListSchema>;
  private plugin = new Plugin({
    props: {
      transformPasted: slice => {
        const { schema } = this.editorView.state;
        const bulletListItemRegex = /^\s*([-+*]\s)/;
        const orderedListItemRegex = /^\s*(\d+)\.\s/;

        let newContent = Fragment.from();
        slice.content.forEach((node, offset, index) => {
          const lastChild = newContent.childCount
            ? newContent.child(newContent.childCount - 1)
            : null;
          const isTextOnlyParagraph =
            ParagraphNode.is(node) &&
            !!node.childCount &&
            [...Array(node.childCount)].every((_, i) => node.child(i).isText);

          if (!isTextOnlyParagraph) {
            newContent = newContent.append(Fragment.from(node));
            return;
          }

          const nodeText = node.textBetween(0, node.content.size);

          if (bulletListItemRegex.test(nodeText)) {
            const [allMatch] = nodeText.match(bulletListItemRegex)!;
            const listItem = schema.nodes.list_item.create(
              null,
              node.replace(0, allMatch.length, Slice.empty),
            );

            if (lastChild && BulletListNode.is(lastChild)) {
              const tr = new Transform(lastChild);
              tr.insert(lastChild.content.size, listItem);
              newContent = newContent.replaceChild(newContent.childCount - 1, tr.doc);
              return;
            } else {
              const list = schema.nodes.bullet_list.createChecked(null, [listItem]);
              newContent = newContent.append(Fragment.from(list));
              return;
            }
          } else if (orderedListItemRegex.test(nodeText)) {
            const [allMatch, orderStr] = nodeText.match(orderedListItemRegex)!;
            const order = parseInt(orderStr, 10);
            const listItem = schema.nodes.list_item.create(
              null,
              node.replace(0, allMatch.length, Slice.empty),
            );

            if (order === 1) {
              const list = schema.nodes.ordered_list.createChecked(null, [listItem]);
              newContent = newContent.append(Fragment.from(list));
              return;
            } else if (lastChild && OrderedListNode.is(lastChild)) {
              if (lastChild.childCount + 1 !== order) {
                newContent = newContent.append(Fragment.from(node));
                return;
              }
              const tr = new Transform(lastChild);
              tr.insert(lastChild.content.size, listItem);
              newContent = newContent.replaceChild(newContent.childCount - 1, tr.doc);
              return;
            }
          }

          newContent = newContent.append(Fragment.from(node));
        });

        return new Slice(newContent, slice.openStart, slice.openEnd);
      },
    },
  });

  public afterEditorInit(editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins(schema: ListSchema) {
    return [
      this.plugin,
      { priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY, plugin: keymap(this.getKeyBindings(schema)) },
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: inputRules({ rules: this.getInputRules(schema) }),
      },
    ];
  }

  public beforeEditorInit(schama: ListSchema) {
    this.api = {
      orderedListService: new ListService(schama.nodes.ordered_list),
      bulletListService: new ListService(schama.nodes.bullet_list),
    };
  }

  private getKeyBindings(schema: ListSchema) {
    const wrapInListBulletKey = isMacOS ? 'Shift-Mod-8' : 'Shift-Ctrl-8';
    const wrapInListOrderedKey = isMacOS ? 'Shift-Mod-9' : 'Shift-Ctrl-9';

    return {
      [wrapInListBulletKey]: wrapInList(schema.nodes.bullet_list),
      [wrapInListOrderedKey]: wrapInList(schema.nodes.ordered_list),
      ['Enter']: splitListItem(schema.nodes.list_item),
      ['Mod-[']: liftListItem(schema.nodes.list_item),
      ['Shift-Tab']: liftListItem(schema.nodes.list_item),
      ['Mod-]']: sinkListItem(schema.nodes.list_item),
      ['Tab']: sinkListItem(schema.nodes.list_item),
    };
  }

  private getInputRules(schema: ListSchema) {
    return [
      wrappingInputRule(/^\s*([-+*])\s$/, schema.nodes.bullet_list),
      wrappingInputRule(
        /^(\d+)\.\s$/,
        schema.nodes.ordered_list,
        match => ({ order: +match[1] }),
        (match, node) => node.childCount + node.attrs.order === parseInt(match[1], 10),
      ),
    ];
  }
}
